import { Usb, Bluetooth } from "lucide-react";
import type { ConnectionType } from "@/store/printerStore";
import type { LucideIcon } from "lucide-react";

/** Active connection modes (WiFi deferred until a packaged bridge ships). */
export type ActiveConnectionType = Exclude<ConnectionType, "wifi">;

interface Props {
  value: ConnectionType;
  onChange: (type: ActiveConnectionType) => void;
  bluetoothDeviceName: string | null;
  onScanBluetooth: () => void;
  isScanning: boolean;
}

const TYPES: { id: ActiveConnectionType; label: string; icon: LucideIcon; sub: string }[] = [
  { id: "usb",       label: "USB",       icon: Usb,       sub: "Windows · Print Helper" },
  { id: "bluetooth", label: "Bluetooth", icon: Bluetooth, sub: "Wireless · Chrome / Edge" },
];

export function ConnectionTypeSelector({
  value, onChange,
  bluetoothDeviceName, onScanBluetooth, isScanning,
}: Props) {
  const selectedId: ActiveConnectionType = value === "bluetooth" ? "bluetooth" : "usb";

  return (
    <section className="border border-gray-200 rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
        <h2 className="text-sm font-medium text-gray-900">Connection type</h2>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {TYPES.map((t) => {
          const Icon = t.icon;
          const selected = selectedId === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-colors ${
                selected
                  ? "border-gray-900 bg-gray-50"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Icon className={`w-5 h-5 ${selected ? "text-gray-900" : "text-gray-400"}`} />
              <span className={`text-sm font-medium ${selected ? "text-gray-900" : "text-gray-500"}`}>
                {t.label}
              </span>
              <span className="text-xs text-gray-400">{t.sub}</span>
            </button>
          );
        })}
      </div>

      {selectedId === "bluetooth" && (
        <div className="flex items-center justify-between pt-1">
          <div>
            <p className="text-sm text-gray-700">
              {bluetoothDeviceName ?? "No device paired"}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Pairing opens the browser&apos;s Bluetooth picker (HTTPS required)
            </p>
          </div>
          <button
            type="button"
            onClick={onScanBluetooth}
            disabled={isScanning}
            className="text-sm px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {isScanning ? "Scanning…" : bluetoothDeviceName ? "Re-pair" : "Pair device"}
          </button>
        </div>
      )}
    </section>
  );
}
